import { Box, IconButton } from '@mui/material';
import { ExpandMoreOutlined } from '@mui/icons-material';
import { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { tokens, usePaloThemeGuard } from '@synergy/theme-palo';

const c = tokens.color;

export interface PaloRecordCardProps {
    /** DOM id, so a jump bar can scroll to the card. */
    id?: string;
    /** What the card is about — "Kunde 1 · Bergbahn AG". */
    title: ReactNode;
    /** Quiet facts beside the title, readable while the card is closed. */
    meta?: ReactNode;
    /** A control that belongs to the card as a whole, e.g. its delivery date. */
    headerControl?: ReactNode;
    /** Actions at the far right of the head, before the toggle. */
    actions?: ReactNode;
    open: boolean;
    onToggle: () => void;
}

/**
 * One of several records inside a document — a customer of an inquiry, a
 * delivery address of an order (#2252, #2256, #2259).
 *
 * The head stays readable when the card is closed. The body stays mounted while
 * closed: its fields are part of the form, so validation and saving still see
 * every record, and a card whose fields fail can be opened by its parent.
 */
export const PaloRecordCard = ({
    id,
    title,
    meta,
    headerControl,
    actions,
    open,
    onToggle,
    children,
}: PropsWithChildren<PaloRecordCardProps>): ReactElement => {
    usePaloThemeGuard('PaloRecordCard');

    return (
        <Box
            id={id}
            sx={{
                border: `1px solid ${c.line}`,
                borderRadius: tokens.radius.default + 'px',
                backgroundColor: c.surface,
                mb: '12px',
                scrollMarginTop: '12px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px 12px',
                    padding: '7px 10px 7px 14px',
                    borderBottom: open ? `1px solid ${c.line}` : 'none',
                    backgroundColor: c.surfaceAlt,
                    borderRadius: open
                        ? `${tokens.radius.default}px ${tokens.radius.default}px 0 0`
                        : tokens.radius.default + 'px',
                }}
            >
                <Box
                    role="button"
                    onClick={onToggle}
                    sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '10px',
                        minWidth: 0,
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 620,
                        color: c.ink,
                    }}
                >
                    <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {title}
                    </Box>
                    {meta && (
                        <Box component="span" sx={{ fontSize: '11.5px', fontWeight: 450, color: c.ink3 }}>
                            {meta}
                        </Box>
                    )}
                </Box>
                {headerControl && <Box sx={{ minWidth: 0 }}>{headerControl}</Box>}
                <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {actions}
                    <IconButton
                        size="small"
                        aria-label={open ? 'Zuklappen' : 'Aufklappen'}
                        aria-expanded={open}
                        onClick={onToggle}
                    >
                        <ExpandMoreOutlined
                            sx={{
                                fontSize: 18,
                                transition: 'transform .15s',
                                transform: open ? 'none' : 'rotate(-90deg)',
                            }}
                        />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ display: open ? 'block' : 'none', padding: '12px 14px' }}>{children}</Box>
        </Box>
    );
};

export interface PaloJumpBarItem {
    key: string | number;
    label: ReactNode;
    /** A count or state beside the label. */
    meta?: ReactNode;
    onClick: () => void;
}

export interface PaloJumpBarProps {
    title: ReactNode;
    items: PaloJumpBarItem[];
    /** E.g. adding another record. */
    action?: ReactNode;
}

/**
 * The records of a document in one line, each a jump to its card — so a long
 * document with closed cards is still crossed in one click (#2252).
 */
export const PaloJumpBar = ({ title, items, action }: PaloJumpBarProps): ReactElement => (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', mb: '10px' }}>
        <Box component="span" sx={{ fontSize: '11px', fontWeight: 520, color: c.ink3, mr: '4px' }}>
            {title}
        </Box>
        {items.map((item, index) => (
            <Box
                key={item.key}
                role="button"
                onClick={item.onClick}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 10px',
                    fontSize: '12px',
                    color: c.ink2,
                    border: `1px solid ${c.line}`,
                    borderRadius: '999px',
                    backgroundColor: c.surface,
                    cursor: 'pointer',
                    maxWidth: 280,
                    '&:hover': { color: c.ink, borderColor: c.lineHard },
                }}
            >
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', color: c.ink3 }}>
                    {index + 1}
                </Box>
                <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.label}
                </Box>
                {undefined !== item.meta && (
                    <Box component="span" sx={{ color: c.ink3 }}>
                        {item.meta}
                    </Box>
                )}
            </Box>
        ))}
        {action}
    </Box>
);
